import {ArrowPathIcon} from "@heroicons/react/24/outline";
import {SegmentedControl} from "../../../general/SegmentedControl.tsx";
import {SplitButton} from "../../../general/SplitButton/index.ts";
import {SecondaryButton} from "../../../general/SecondaryButton.tsx";
import {fieldLabelClasses} from "../../../general/fieldStyles.ts";
import {ViewModeEnum} from "../types";
import {
    EXPORT_STORAGE_KEY,
    exportButtonLabel,
    exportMenu,
    isExportOption,
    type ComparisonMode,
    type ExportOption,
} from "../utils";
import {AccountSearchInput} from "./AccountSearchInput.tsx";
import {ComparisonPicker} from "./ComparisonPicker.tsx";
import {CurrencyField} from "./CurrencyField.tsx";
import {ExpandCollapseButton} from "./ExpandCollapseButton.tsx";
import {PeriodDateRangeField} from "./PeriodDateRangeField.tsx";
import {PredictedAccountsToggle} from "./PredictedAccountsToggle.tsx";
import type { CurrencyEnum } from '../../../../generated/enums.ts';

const EXPORT_PRIMARY_LABEL = "Exporteren";

const VIEW_MODE_OPTIONS = [
    {value: ViewModeEnum.SLIM, label: "Slim"},
    {value: ViewModeEnum.CLASSIC, label: "Klassiek"},
];

interface TrialBalanceToolbarProps {
    viewMode: ViewModeEnum;
    onViewModeChange: (mode: ViewModeEnum) => void;
    dateFrom: string;
    dateTo: string;
    onDateRangeChange: (from: string, to: string) => void;
    selectedCurrency: CurrencyEnum;
    currencyOptions: CurrencyEnum[];
    onCurrencyChange: (currency: CurrencyEnum) => void;
    showMultiCurrencyBanner: boolean;
    comparisonMode: ComparisonMode;
    onComparisonChange: (mode: ComparisonMode) => void;
    showPredictedAccounts: boolean;
    onPredictedChange: (value: boolean) => void;
    searchQuery: string;
    onSearchChange: (value: string) => void;
    onRefresh: () => void;
    isRefreshingCache: boolean;
    isLoading: boolean;
    beyondDefault: boolean;
    onToggleAll: () => void;
    canExportProfitAndLossPdf: boolean;
    canExportTrialBalanceJson: boolean;
    exportDisabled: boolean;
    isExporting: boolean;
    onExport: (option: ExportOption) => void;
}

export function TrialBalanceToolbar({
    viewMode,
    onViewModeChange,
    dateFrom,
    dateTo,
    onDateRangeChange,
    selectedCurrency,
    currencyOptions,
    onCurrencyChange,
    showMultiCurrencyBanner,
    comparisonMode,
    onComparisonChange,
    showPredictedAccounts,
    onPredictedChange,
    searchQuery,
    onSearchChange,
    onRefresh,
    isRefreshingCache,
    isLoading,
    beyondDefault,
    onToggleAll,
    canExportProfitAndLossPdf,
    canExportTrialBalanceJson,
    exportDisabled,
    isExporting,
    onExport,
}: TrialBalanceToolbarProps) {
    const exportOptions = exportMenu(canExportProfitAndLossPdf, canExportTrialBalanceJson);
    return (
        <div className="mb-6 space-y-3.5">
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1.5">
                    <span className={fieldLabelClasses}>Weergave</span>
                    <SegmentedControl
                        value={viewMode}
                        options={VIEW_MODE_OPTIONS}
                        onChange={onViewModeChange}
                        ariaLabel="Weergave"
                    />
                </div>

                <PeriodDateRangeField
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onChange={onDateRangeChange}
                />

                <CurrencyField
                    selectedCurrency={selectedCurrency}
                    options={currencyOptions}
                    onChange={onCurrencyChange}
                    showMultiCurrencyBanner={showMultiCurrencyBanner}
                />

                <ComparisonPicker
                    value={comparisonMode}
                    currentRange={{from: dateFrom, to: dateTo}}
                    onChange={onComparisonChange}
                />

                <PredictedAccountsToggle
                    checked={showPredictedAccounts}
                    onChange={onPredictedChange}
                />
            </div>

            <div className="flex flex-wrap items-end justify-between gap-3">
                <AccountSearchInput value={searchQuery} onChange={onSearchChange} />

                <div className="flex flex-wrap items-center gap-2">
                    <SecondaryButton
                        title="Vernieuwen"
                        icon={ArrowPathIcon}
                        onClick={onRefresh}
                        disabled={isRefreshingCache || isLoading || !dateFrom || !dateTo}
                        loading={isRefreshingCache}
                        tooltip="Berekent de proefbalans opnieuw voor deze klant. Gebruik dit als de cijfers mogelijk verouderd zijn."
                    />
                    <ExpandCollapseButton
                        beyondDefault={beyondDefault}
                        disabled={!!searchQuery.trim()}
                        onClick={onToggleAll}
                    />
                    <SplitButton
                        key={EXPORT_STORAGE_KEY}
                        storageKey={EXPORT_STORAGE_KEY}
                        options={exportOptions}
                        defaultOptionId={exportOptions[0].id}
                        labelForSelected={(selected) => exportButtonLabel(EXPORT_PRIMARY_LABEL, selected)}
                        disabled={exportDisabled}
                        pending={isExporting}
                        primaryLabel={EXPORT_PRIMARY_LABEL}
                        pendingLabel="Bezig…"
                        menuButtonAriaLabel="Meer opties"
                        onClick={(optionId) => {
                            if (isExportOption(optionId)) onExport(optionId);
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
